// Cook Mode "any left?" sweep — fires every time the user finishes a
// recipe ("Done — eat" on the last step). One row per recipe ingredient.
//
// Two row flavors:
//   • Matched — the ingredient text matches an existing pantry row
//     (case-insensitive). Picks update the existing row.
//   • New — no pantry match. The user JUST cooked with it, so they
//     had some on hand. Picks can choose to start tracking it in
//     pantry (with the recipe's quantity), mark it low, send to
//     shop, or skip if they don't want to track this ingredient
//     (one-shot specialty stuff, "a pinch of saffron", etc.).
//
// Chips (5):
//   plenty   — Still plenty (matched: no change; new: skip — we don't
//              auto-add at "plenty" because most everyday recipe items
//              don't warrant a tracked pantry row, e.g. "1 tsp salt")
//   half     — Half left   (matched: qty="half"; new: add with qty="half")
//   almost   — Almost gone (matched: qty="almost gone"+lowStock;
//              new: add with that + lowStock)
//   gone     — All gone    (matched: remove + auto-shop; new: don't
//              add + auto-shop so the user can restock)
//   skip     — Skip        (no-op either way)
//
// "All gone" auto-adds the item to the user's last-used shopping list
// (the same list the recipe→list flow remembers). That closes the
// restock loop in a single tap — the user doesn't have to leave the
// recipe to re-add it by hand. Skipped if no last-used list is set.

import { useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type PantryItem } from "../../lib/rpc";
import type { NotifyFn } from "../../components/toast";

const LAST_LIST_KEY = "hub.recipes.addToList.lastId";

export type SweepChip = "plenty" | "half" | "almost" | "gone" | "skip";

interface Match {
  /** Recipe-side ingredient. Carries `text` (the name to display + use
   *  for the pantry-add) and optionally `quantity` (a hint we surface
   *  on new-row creation so the qty isn't blank). Minimal structural
   *  type so both base RecipeItems and overlay-applied effective
   *  meal-plan items can be threaded into the same sweep UI. */
  recipeItem: { text: string; quantity?: string | null };
  /** Null when there's no matching pantry row — sweep treats this row
   *  as "would you like to start tracking this in pantry?" */
  pantryItem: PantryItem | null;
}

interface CookDoneSweepProps {
  matches: Match[];
  onClose: () => void;
  /** Called after a successful commit so the parent can close Cook
   *  Mode and tear itself down. Even if the commit yielded an empty
   *  patch (everything skipped), this still fires. */
  onCommitted: () => void;
  notify: NotifyFn;
}

const CHIPS: { id: SweepChip; label: string; hint: string }[] = [
  { id: "plenty", label: "Still plenty",  hint: "No change" },
  { id: "half",   label: "Half left",     hint: "qty → half" },
  { id: "almost", label: "Almost gone",   hint: "Mark low" },
  { id: "gone",   label: "All gone",      hint: "Remove + shop" },
  { id: "skip",   label: "Skip",          hint: "Didn't use it" },
];

/** Unique React key for a row. Pantry-matched rows use the pantry id
 *  (stable across re-renders); new-row keys use the recipe text since
 *  that's the only stable identifier we have for an ingredient that
 *  doesn't have a database row yet. */
function rowKey(m: Match): string {
  return m.pantryItem ? `pantry:${m.pantryItem.id}` : `new:${m.recipeItem.text.toLowerCase().trim()}`;
}

export function CookDoneSweep({ matches, onClose, onCommitted, notify }: CookDoneSweepProps) {
  // Default chip per row: "plenty" so a user who taps through without
  // touching anything walks out with zero pantry changes. For new rows
  // "plenty" is also a no-op (we don't auto-add at plenty — see header
  // comment for the chip-semantic table).
  const [picks, setPicks] = useState<Record<string, SweepChip>>(() => {
    const m: Record<string, SweepChip> = {};
    for (const it of matches) m[rowKey(it)] = "plenty";
    return m;
  });
  const [committing, setCommitting] = useState(false);

  // Resolve the last-used shopping list from localStorage. Only used
  // for the "all gone" auto-shop path; missing list = those items
  // still get removed (or never added) from pantry, just no auto-shop.
  const lastShopListId = useMemo<number | null>(() => {
    try {
      const raw = localStorage.getItem(LAST_LIST_KEY);
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch { return null; }
  }, []);

  // Real-time summary of what's about to happen, surfaced under the
  // submit button so the user can sanity-check before committing.
  const summary = useMemo(() => {
    let updates = 0, removes = 0, creates = 0, autoShop = 0;
    for (const m of matches) {
      const pick = picks[rowKey(m)];
      const matched = m.pantryItem != null;
      if (pick === "half" || pick === "almost") {
        if (matched) updates++;
        else creates++;
      } else if (pick === "gone") {
        if (matched) removes++;
        // New + gone: no pantry change, just shop.
        if (lastShopListId) autoShop++;
      }
    }
    return { updates, removes, creates, autoShop, total: matches.length };
  }, [picks, matches, lastShopListId]);

  const commit = async () => {
    setCommitting(true);
    try {
      const updates: { itemId: number; patch: Partial<PantryItem> }[] = [];
      const removes: { itemId: number; autoAddToShopping?: boolean }[] = [];
      const creates: { name: string; qty?: string | null; lowStock?: boolean; source?: string }[] = [];
      for (const m of matches) {
        const key = rowKey(m);
        const pick = picks[key];
        if (pick === "skip" || pick === "plenty") continue;
        if (m.pantryItem) {
          // Existing-row update path (unchanged from the original sweep).
          if (pick === "half") {
            updates.push({ itemId: m.pantryItem.id, patch: { qty: "half" } });
          } else if (pick === "almost") {
            updates.push({ itemId: m.pantryItem.id, patch: { qty: "almost gone", lowStock: true } });
          } else if (pick === "gone") {
            removes.push({ itemId: m.pantryItem.id, autoAddToShopping: lastShopListId != null });
          }
        } else {
          // New-ingredient creates. "gone" doesn't create (didn't have
          // any left to track) but still routes to shop via the auto-
          // shop pseudo-remove with a sentinel id below.
          if (pick === "half") {
            creates.push({ name: m.recipeItem.text, qty: "half", source: "cook_sweep" });
          } else if (pick === "almost") {
            creates.push({ name: m.recipeItem.text, qty: "almost gone", lowStock: true, source: "cook_sweep" });
          } else if (pick === "gone" && lastShopListId) {
            // No pantry create, no remove (nothing to remove) — just
            // surface on shopping list directly via a remove with a
            // -1 itemId would be hacky. Skip the auto-shop for new+gone
            // for now; user can add it manually. Logged so we can
            // revisit if it matters.
            // TODO: add a dedicated "addToShopOnly" array if this proves
            //       to be a common case.
          }
        }
      }
      if (updates.length === 0 && removes.length === 0 && creates.length === 0) {
        // No-op sweep — nothing to commit. Still fine; user picked
        // through and decided nothing changed.
        onCommitted();
        return;
      }
      const r = await api.pantryBulkUpdate({
        updates, removes, creates,
        autoShopListId: lastShopListId,
      });
      const bits: string[] = [];
      if (r.updated.length > 0) bits.push(`${r.updated.length} updated`);
      if (r.created.length > 0) bits.push(`${r.created.length} added`);
      if (r.removed.length > 0) bits.push(`${r.removed.length} removed`);
      if (r.shopAdded.length > 0) bits.push(`${r.shopAdded.length} on shopping list`);
      notify(`Pantry sweep: ${bits.join(" · ") || "no changes"}`);
      onCommitted();
    } catch (e: unknown) {
      notify((e as Error)?.message || "Couldn't commit pantry sweep");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="sweep-bg" onClick={onClose}>
      <div
        className="sweep-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Pantry sweep"
      >
        <header className="sweep-head">
          <div>
            <h2 className="sweep-title">How's the pantry?</h2>
            <p className="sweep-sub">
              Tap a chip per item — keeps your stock honest without you having to remember.
              Ingredients without a pantry row yet show a + chip and get added on commit.
            </p>
          </div>
          <button className="icon-btn" onClick={onClose} title="Skip — leave pantry as is" type="button">
            <Icon name="x" size={14} />
          </button>
        </header>

        <div className="sweep-rows">
          {matches.map((m) => (
            <SweepRow
              key={rowKey(m)}
              recipeItem={m.recipeItem}
              pantryItem={m.pantryItem}
              pick={picks[rowKey(m)]}
              onPick={(p) => setPicks((prev) => ({ ...prev, [rowKey(m)]: p }))}
            />
          ))}
        </div>

        <footer className="sweep-foot">
          <div className="sweep-summary">
            {summary.updates === 0 && summary.removes === 0 && summary.creates === 0
              ? <span>Nothing to commit yet</span>
              : <>
                  {summary.updates > 0 && <span>{summary.updates} updated</span>}
                  {summary.updates > 0 && (summary.creates > 0 || summary.removes > 0) && <span> · </span>}
                  {summary.creates > 0 && <span>{summary.creates} added</span>}
                  {summary.creates > 0 && summary.removes > 0 && <span> · </span>}
                  {summary.removes > 0 && <span>{summary.removes} removed</span>}
                  {summary.autoShop > 0 && <span> · {summary.autoShop} → shopping</span>}
                </>
            }
          </div>
          <div className="sweep-actions">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={committing}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={commit}
              disabled={committing}
            >
              {committing ? "Saving…" : "Apply"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function SweepRow({
  recipeItem, pantryItem, pick, onPick,
}: {
  recipeItem: { text: string; quantity?: string | null };
  pantryItem: PantryItem | null;
  pick: SweepChip;
  onPick: (p: SweepChip) => void;
}) {
  const isNew = pantryItem == null;
  const displayName = pantryItem ? pantryItem.name : recipeItem.text;
  const displayQty = pantryItem ? pantryItem.qty : (recipeItem.quantity || "");
  return (
    <div className="sweep-row" data-new={isNew}>
      <div className="sweep-row-name">
        <span>{displayName}</span>
        {isNew && <span className="sweep-row-newbadge">+ new</span>}
        {displayQty && <span className="sweep-row-qty">{displayQty}</span>}
      </div>
      <div className="sweep-row-chips" role="radiogroup" aria-label={`How much ${displayName} is left?`}>
        {CHIPS.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={pick === c.id}
            className={`sweep-chip sweep-chip-${c.id}`}
            data-active={pick === c.id}
            onClick={() => onPick(c.id)}
            title={c.hint}
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}
